/** @typedef {{ source: string, destination: string }} RewriteRule */

/**
 * Regex fragments for top-level paths that must not be rewritten into
 * `app/t/<tenantId>/`. Add a segment only when a route or asset must keep its
 * existing App Router location (API proxy, Cognito handlers, sitemaps, static
 * files, the internal `/t/` tree, etc.).
 *
 * Tenant-facing pages do not belong here; they live under `app/t/<id>/` and are
 * reached via the catch-all rewrite in `getTenantPublicPathRewrites()`. This
 * list should stay small and infrastructure-focused — everything not matched
 * here is rewritten. See `tenantRewrites.test.ts` for the contract and
 * `app/t/README.md` for the overall routing model.
 */
const TENANT_REWRITE_DENYLIST_REGEX_PARTS = [
    'status(?:/)?$',
    'api(?:/|$)',
    'auth(?:/|$)',
    'access(?:/|$)',
    't(?:/|$)',
    '_next(?:/|$)',
    'revalidate(?:/|$)',
    'redirect$',
    'robots\\.txt$',
    'store-sitemap\\.xml$',
    'sku-refresh\\.sitemap\\.xml$',
    'in-person-skus-sitemap\\.xml$',
    'favicon\\.ico$',
    'well-known(?:/|$)',
    '.*\\.(?:js|png|jpg|jpeg|gif|svg|ico|webp|avif)$',
]
const TENANT_REWRITE_DENYLIST_PATH_PATTERN = TENANT_REWRITE_DENYLIST_REGEX_PARTS.join('|')
const TENANT_REWRITE_DENYLIST_PATHNAME_REGEX = new RegExp(`^/(?:${TENANT_REWRITE_DENYLIST_REGEX_PARTS.join('|')})`)

function resolveActiveTenantId() {
    // TODO(GM-6520): require NEXT_PUBLIC_GM_TENANT_ID and fail fast when unset.
    return process.env.NEXT_PUBLIC_GM_TENANT_ID?.trim()
}

/**
 * Paths that keep top-level App Router / route-handler behavior (API proxy,
 * Cognito handlers, sitemaps, etc.) instead of rewriting into `app/t/<id>/`.
 */
function shouldSkipTenantRewrite(pathname) {
    return TENANT_REWRITE_DENYLIST_PATHNAME_REGEX.test(pathname)
}

function isAllowedTenantTreePath(pathname) {
    const tenantId = resolveActiveTenantId()
    if (!tenantId) {
        return false
    }
    const prefix = `/t/${tenantId}`
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/**
 * Regex used by next.config `/:path(...)` catch-all rewrites:
 * match everything except the denylisted infrastructure paths.
 */
function tenantRewritePathPattern() {
    return `(?!${TENANT_REWRITE_DENYLIST_PATH_PATTERN}).*`
}

/** @returns {RewriteRule[]} */
function getTenantPublicPathRewrites() {
    const tenantId = resolveActiveTenantId()
    // Do not rewrite paths when no tenant is active.
    // TODO(GM-6520): require NEXT_PUBLIC_GM_TENANT_ID and fail fast when unset.
    if (!tenantId) {
        return []
    }
    const destinationPrefix = `/t/${tenantId}`
    const pathPattern = tenantRewritePathPattern()

    /** @type {RewriteRule[]} */
    const rewrites = []

    // App Router serves `app/favicon.ico` at `/favicon.ico`; send Walmart
    // deploys to the tenant asset so bare favicon requests are not GM-branded.
    if (tenantId === 'walmart') {
        rewrites.push({
            source: '/favicon.ico',
            destination: '/tenants/walmart/favicons/favicon.ico',
        })
    }

    rewrites.push(
        {
            source: '/',
            destination: destinationPrefix,
        },
        {
            source: `/:path(${pathPattern})`,
            destination: `${destinationPrefix}/:path*`,
        }
    )

    return rewrites
}

module.exports = {
    TENANT_REWRITE_DENYLIST_PATH_PATTERN,
    resolveActiveTenantId,
    shouldSkipTenantRewrite,
    isAllowedTenantTreePath,
    getTenantPublicPathRewrites,
}
